<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'

import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import IconImage from '@lahaus-roomie/roomie/src/assets/icons/image.svg'
import RoomieNavigationButton from '@lahaus-roomie/roomie/src/components/RoomieNavigationButton/index.vue'
import BackIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/back.svg'
import NextIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/next.svg'

import PhotoSwipeLightbox from 'photoswipe/lightbox'
import Photoswipe from 'photoswipe'
import 'photoswipe/style.css'

import type { Props, Source } from './types'

const { t } = useI18n()
const { $useLhId } = useNuxtApp()

const props = withDefaults(defineProps<Props>(), {
  imageMultiplier: 5,
  itemsPerSlide: 1,
  showMobileArrows: false,
  navigationButtonSize: 'sm',
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})
const emit = defineEmits(['imageClicked', 'imageScrolled'])

const picturesTouched = ref(false)
const sliderGallery = ref()

const picturesSize = ref([{ width: 0, height: 0 }])

const indexImage = ref(0)

const lightbox = ref()

const scrollOffset = ref(0)

const resolvePicturesLoad = () => {
  const { zoom, smallLoad, picturesSources, itemsPerSlide } = props
  return !zoom && smallLoad
    ? picturesSources.slice(0, 2 * itemsPerSlide + 1)
    : picturesSources
}

const pictures = ref(resolvePicturesLoad())

const updatePictures = () => {
  if (!picturesTouched.value) {
    pictures.value = props.picturesSources
    picturesTouched.value = true
  }
}

const handleScroll = () => {
  if (!sliderGallery.value) return

  scrollOffset.value = sliderGallery.value.scrollLeft
  const imageWidth = sliderGallery.value.offsetWidth / props.itemsPerSlide
  indexImage.value = Math.round(scrollOffset.value / imageWidth)

  emit('imageScrolled', indexImage.value)
}

const scrollEvent = () => {
  updatePictures()
  handleScroll()
}

const refreshPictures = (reset: boolean) => {
  if (!picturesTouched.value && !reset) {
    pictures.value = props.picturesSources
    picturesTouched.value = true
  }
}

const getScrollDistance = (direction: string, imageWidth: number) => {
  return direction === 'left' ? -imageWidth : imageWidth
}

const handleReset = () => {
  indexImage.value = 0
  picturesTouched.value = false
}

const scrollGallery = (scrollDistance: number, reset: boolean) => {
  if (sliderGallery.value?.scrollBy) {
    sliderGallery.value.scrollBy({
      left: reset ? 0 : scrollDistance,
      behavior: 'smooth'
    })
  }
}

const changeImage = (direction: string, reset?: boolean) => {
  refreshPictures(!!reset)

  if (reset) {
    handleReset()
  }

  if (sliderGallery.value) {
    const imageWidth = sliderGallery.value.offsetWidth / props.itemsPerSlide
    const scrollDistance = getScrollDistance(direction, imageWidth)
    scrollGallery(scrollDistance, !!reset)

    return 'Slider gallery'
  }

  return 'No slider gallery'
}

const getPictureSize = () => {
  const elements = document.getElementsByClassName('carrousel-picture-class')

  if (elements?.length > 0) {
    picturesSize.value = []

    const calculateDimensions = (element: HTMLElement) => {
      const width = element.offsetWidth * props.imageMultiplier
      const height = element.offsetHeight * props.imageMultiplier
      return { width, height }
    }

    for (const element of elements) {
      const dimensions = calculateDimensions(element as HTMLElement)

      if (props.sizeReverse) {
        picturesSize.value.unshift(dimensions)
      } else {
        picturesSize.value.push(dimensions)
      }
    }
  }
}

const handleImageClick = (index: number) => {
  if (props.zoom) {
    getPictureSize()
  }
  emitImageClickEvent(index)
}

const emitImageClickEvent = (index: number) => {
  emit('imageClicked', index)
}

const resetScrollPosition = () => {
  if (sliderGallery.value?.scrollTo) {
    sliderGallery.value.scrollTo(0, 0)
  }
}

const resetImagePosition = () => {
  changeImage('left', true)
}

const resetPosition = () => {
  resetScrollPosition()
  resetImagePosition()
}

const getLightboxConfig = () => ({
  gallerySelector: '#app-carrousel-picture-container',
  childSelector: 'a',
  initialZoomLevel: 'fit',
  secondaryZoomLevel: 1.4,
  maxZoomLevel: 3,
  hideAnimationDuration: 0,
  pswpModule: Photoswipe,
  wheelToZoom: true,
  preload: [1, 2]
})

const initializeLightbox = () => {
  lightbox.value = new PhotoSwipeLightbox(getLightboxConfig())
  lightbox.value.init()
}

const initLightbox = () => {
  if (props.zoom) {
    initializeLightbox()
  }
}

const destroyLightbox = () => {
  if (!lightbox.value) return

  lightbox.value?.destroy()
  lightbox.value = null
}

const getLastElement = (array: T[]) => {
  return array.length > 0 ? array[array.length - 1] : undefined
}

const getHref = (pictureSource: Source[]) => {
  const lastElement = getLastElement(pictureSource)
  return lastElement?.src
}

const appendQueryParam = (url: URLSearchParams, key: string, value?: number) => {
  if (value !== undefined) {
    url.append(key, value.toString())
  }
}

const getFormatImage = (picture: Source) => {
  const pictureUrl = new URLSearchParams(picture.src as string)

  appendQueryParam(pictureUrl, 'w', picture.imageWidth)
  appendQueryParam(pictureUrl, 'h', picture.imageHeight)

  return pictureUrl.toString()
}

const watchPicturesSources = () => {
  watch(() => props.picturesSources, resetPosition)
}

watchPicturesSources()

const initialize = () => {
  initLightbox()
  resetPosition()
}

onMounted(initialize)

const cleanup = () => {
  destroyLightbox()
}

onUnmounted(cleanup)

const calculateWidth = (itemsPerSlide: number) => (1 / itemsPerSlide) * 100

const width = computed(() => calculateWidth(props.itemsPerSlide))

</script>

<script lang="ts">
export default {
  name: 'AppCarrousel'
}
</script>

<template>
  <div
    :id="id"
    class="app-carrousel scrollbar-hidden flex overflow-auto relative">
    <RoomieNavigationButton
      v-if="picturesSources?.length > 0"
      :id="`app-carrousel-left-button-${id}`"
      :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'desplazar', C: 'atras'})"
      class="app-carrousel-button--left duration-300 absolute left-4 top-1/2 -translate-y-1/2 z-10"
      :class="{
        'opacity-0 cursor-default': indexImage === 0,
        'hidden': !showMobileArrows
      }"
      aria-label="ir a la imagen anterior"
      :size="navigationButtonSize"
      variant="outlined"
      @click="changeImage('left')">
      <BackIcon class="pointer-events-none" />
    </RoomieNavigationButton>

    <div
      id="app-carrousel-picture-container"
      ref="sliderGallery"
      class="app-carrousel-picture-container scrollbar-hidden flex items-center overflow-auto"
      :class="pictureContainerStyles"
      @scroll="scrollEvent">
      <figure
        v-for="(pictureSource, index) in pictures"
        :key="index"
        class="w-full h-full flex items-center relative flex-[0_0_auto]"
        :class="{ 'app-carrousel--opacity': showSeeMoreCta && index === picturesSources.length - 1}"
        :style="[figureStyles, { width: `${width}%` }]">
        <component
          :is="zoom ? 'a' : 'div'"
          :id="`app-carrousel-picture-link-${index}`"
          class="app-carrousel-picture-link"
          :class="anchorStyles"
          :href="getHref(pictureSource)"
          :data-pswp-width="picturesSize?.[index]?.width"
          :data-pswp-height="picturesSize?.[index]?.height"
          @click="handleImageClick(index)">
          <AppImage
            alt="imagenes proyecto"
            :sources="pictureSource"
            class="flex w-full"
            :class-img="['carrousel-picture-class mx-auto object-contain pointer-events-none', imgStyles as string]"
            :src="getFormatImage(pictureSource[0])"
            :data-lh-id="$useLhId({ A: 'ver', C: 'fotos', ...dataAttributes.dataLh, I: index})" />

          <RoomieButton
            v-if="showSeeMoreCta && index === picturesSources.length - 1"
            :id="`app-carrousel-button-see-more-${id}`"
            :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'ver', C: t('seeMore'), I: index})"
            :aria-label="t('seeMore')"
            class="!bg-white font-medium absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 whitespace-nowrap"
            :class="{ '!cursor-default': index !== picturesSources.length - 1 }"
            variant="outlined"
            icon-placement="left">
            <template #icon>
              <IconImage />
            </template>

            <span> {{ t('seeMore') }} </span>
          </RoomieButton>
        </component>
      </figure>
    </div>

    <RoomieNavigationButton
      v-if="picturesSources.length > 1"
      :id="`app-carrousel-right-button-${id}`"
      :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'desplazar', C: 'siguiente'})"
      class="app-carrousel-button--right duration-300 absolute right-4 top-1/2 -translate-y-1/2 z-10"
      :class="{
        'opacity-0 cursor-default': indexImage + props.itemsPerSlide === picturesSources.length,
        'hidden': !showMobileArrows
      }"
      aria-label="ir a la imagen siguiente"
      :size="navigationButtonSize"
      variant="outlined"
      @click="changeImage('right')">
      <NextIcon class="pointer-events-none" />
    </RoomieNavigationButton>
  </div>
</template>

<style lang="scss">
.lh-roomie-v2 {
  .app-carrousel {
    min-height: 40.4rem;

    &-picture-container {
      @apply overflow-y-hidden;

      min-width: 100%;
      scroll-snap-type: x mandatory;
      scroll-snap-stop: always;

      figure {
        scroll-snap-align: center;
        max-height: 50rem;
      }
    }

    .carrousel-picture-class {
      border-radius: 24px;
    }

    @media (width >= 1024px) {
      min-height: 50rem;
    }

    .right-svg {
      position: relative;
      left: -38px;
    }

    .app-carrousel-button--left {
      @apply hidden rounded-full;
    }

    .app-carrousel-button--right {
      @apply hidden rounded-full;
    }

    &:hover {
      @media (width >= theme('screens.lg')) {
        .app-carrousel-button--left {
          @apply block;
        }

        .app-carrousel-button--right {
          @apply block;
        }
      }
    }
  }

  .app-carrousel--opacity {
    @apply relative;

    &::before {
      @apply absolute w-full h-full bg-carbon-800 bg-opacity-50;

      content: '';
    }
  }

  .pswp {
    &__button {
      &--arrow--next,
      &--arrow--prev {
        @apply w-50 h-50 mx-16;
        @apply bg-white rounded-full shadow-[0_0_10px_#0000004f];
        @apply grid place-content-center;
        @apply hover:bg-white;
      }

      &--arrow--next {
        & .pswp__icn {
          @apply fill-primary-600 top-10 -left-4 scale-[.6] rotate-180;
        }
      }

      &--arrow--prev {
        & .pswp__icn {
          @apply fill-primary-600 top-10 -left-0 scale-[.6];
        }
      }
    }

    &__icn-shadow {
      @apply hidden;
    }

    &__counter {
      @apply text-18 font-medium;
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
